"use client";
import {
  Background,
  Controls,
  MiniMap,
  type NodeMouseHandler,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import "./pipeline-graph.css";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { AdminNodeAction, RunProgress } from "@/types/aicheck";
import { DirectionContext, type LayoutDirection } from "./DirectionContext";
import { PipelineEdge } from "./edges/PipelineEdge";
import { usePipelineGraph } from "./hooks/usePipelineGraph";
import { AICheckNode } from "./nodes/AICheckNode";
import { ConsistencyNode } from "./nodes/ConsistencyNode";
import { SummaryNode } from "./nodes/SummaryNode";
import { SystemNode } from "./nodes/SystemNode";
import type { PipelineNode, PipelineNodeStatus } from "./types";

/** decoratedNodes の再利用判定に使う、前回作ったノードとその入力。 */
interface DecoratedNodeEntry {
  source: PipelineNode;
  decorated: PipelineNode;
  nodeExecutionDirectives: RunProgress["nodeExecutionDirectives"];
  onAdminAction: PipelineGraphProps["onAdminAction"];
  onRetryFailed: PipelineGraphProps["onRetryFailed"];
}

const MINIMAP_NODE_COLORS: Record<PipelineNodeStatus, string> = {
  planned: "#e5e7eb",
  pending: "#d1d5db",
  active: "#60a5fa",
  completed: "#34d399",
  failed: "#f87171",
  aborted: "#fb923c",
  blocked: "#94a3b8",
  skipped: "#e5e7eb",
};

export interface PipelineGraphProps {
  progress: RunProgress;
  onCancel?: () => void;
  onRetry?: () => void;
  onRetryFailed?: () => void;
  onAdminAction?: (action: AdminNodeAction, nodeId: string) => void;
  onDismiss?: () => void;
  onCollapse?: () => void;
}

const nodeTypes = {
  system: SystemNode,
  "ai-check": AICheckNode,
  consistency: ConsistencyNode,
  summary: SummaryNode,
};

const edgeTypes = {
  pipeline: PipelineEdge,
};

const MIN_HEIGHT = 200;
const COLLAPSE_THRESHOLD = 100;

/**
 * **ReactFlow に渡すオブジェクトは使い回す（毎回作らない）。**
 *
 * React Flow の StoreUpdater は「追跡対象の prop」を前回値と `!==` で比べ、
 * 違えば内部ストアへ書き戻す。`fitViewOptions` はその追跡対象なので、
 * レンダーのたびに `{ padding: 0.08 }` を作っていると
 *
 *   render → prop の identity が変わる → store.setState →
 *   購読側が再レンダー → また新しいオブジェクト → …
 *
 * が止まらなくなる（React #185: Maximum update depth exceeded）。
 * モジュール定数にして identity を固定する。
 */
const FIT_VIEW_OPTIONS = { padding: 0.08 } as const;
const PRO_OPTIONS = { hideAttribution: true } as const;
const MINIMAP_STYLE = { height: 80, width: 120 } as const;

function DirectionToggle({
  direction,
  onChange,
}: {
  direction: LayoutDirection;
  onChange: (d: LayoutDirection) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(direction === "TB" ? "LR" : "TB")}
      className="absolute top-2 right-2 z-10 flex items-center gap-1 rounded border border-gray-200 bg-white/90 px-2 py-1 text-[10px] font-medium text-gray-500 shadow-sm hover:bg-gray-50 hover:text-gray-700 transition-colors"
      title={
        direction === "TB"
          ? "Switch to left-to-right"
          : "Switch to top-to-bottom"
      }
    >
      {direction === "TB" ? (
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M3 5h8M7 5v6M4 11l3 2 3-2"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : (
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M5 3v8M5 7h6M11 4l2 3-2 3"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
      {direction === "TB" ? "TB" : "LR"}
    </button>
  );
}

function PipelineGraphInner({
  progress,
  direction,
  onRetryFailed,
  onAdminAction,
  onCollapse,
}: PipelineGraphProps & { direction: LayoutDirection }) {
  const { nodes, edges, onNodesChange, graphHeight, skeletonVersion } =
    usePipelineGraph(progress, direction);
  const { fitView } = useReactFlow();
  const prevSkeletonRef = useRef(skeletonVersion);

  // Re-fit when skeleton changes (new nodes added)
  useEffect(() => {
    if (skeletonVersion !== prevSkeletonRef.current) {
      prevSkeletonRef.current = skeletonVersion;
      const timer = setTimeout(() => fitView(FIT_VIEW_OPTIONS), 50);
      return () => clearTimeout(timer);
    }
  }, [skeletonVersion, fitView]);

  // Computed default height — scaled to fit graph content
  const defaultHeight = useMemo(
    () =>
      Math.max(MIN_HEIGHT, Math.min(Math.round(graphHeight * 0.45 + 60), 700)),
    [graphHeight],
  );

  const [userHeight, setUserHeight] = useState<number | null>(null);
  const height = userHeight ?? defaultHeight;

  // Reset userHeight when default changes (skeleton rebuild)
  const prevDefaultRef = useRef(defaultHeight);
  if (prevDefaultRef.current !== defaultHeight) {
    prevDefaultRef.current = defaultHeight;
    if (userHeight !== null) setUserHeight(null);
  }

  // --- Drag-to-resize (ref-based to avoid circular deps) ---
  const dragRef = useRef({ startY: 0, startH: 0 });
  const collapseRef = useRef(onCollapse);
  collapseRef.current = onCollapse;

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      dragRef.current = {
        startY: e.clientY,
        startH: userHeight ?? defaultHeight,
      };

      const onMove = (ev: PointerEvent) => {
        const dy = ev.clientY - dragRef.current.startY;
        const newH = dragRef.current.startH + dy;
        if (newH < COLLAPSE_THRESHOLD) {
          document.removeEventListener("pointermove", onMove);
          document.removeEventListener("pointerup", onUp);
          collapseRef.current?.();
          return;
        }
        setUserHeight(Math.max(MIN_HEIGHT, newH));
      };
      const onUp = () => {
        document.removeEventListener("pointermove", onMove);
        document.removeEventListener("pointerup", onUp);
      };

      document.addEventListener("pointermove", onMove);
      document.addEventListener("pointerup", onUp);
    },
    [userHeight, defaultHeight],
  );

  // **ノードのオブジェクトは、中身が変わったときだけ作り直す。**
  //
  // React Flow は `nodes` prop の各要素を identity で見比べ、前回と同じ
  // オブジェクトなら内部ストアの実測サイズ（`measured`）を引き継ぎ、
  // 違えば「別のノード」とみなして測り直す（`adoptUserNodes` の
  // `checkEquality`）。ここは以前、毎レンダーで全ノードを新しい
  // オブジェクトに作り直していたため、
  //
  //   props→store 同期 → 全ノード測り直し → onNodesChange →
  //   nodes 更新 → また全ノード作り直し → …
  //
  // が互いを呼び合い、更新が止まらなくなることがあった
  // （React #185: Maximum update depth exceeded）。
  //
  // 元のノードと注入する値がどちらも前回と同じなら、前回作った
  // オブジェクトをそのまま返して輪を切る。表示内容は変わらない。
  const decorationCacheRef = useRef(new Map<string, DecoratedNodeEntry>());
  const decoratedNodes = useMemo(() => {
    const cache = decorationCacheRef.current;
    const nextCache = new Map<string, DecoratedNodeEntry>();
    const result = nodes.map((node) => {
      const cached = cache.get(node.id);
      if (
        cached &&
        cached.source === node &&
        cached.nodeExecutionDirectives === progress.nodeExecutionDirectives &&
        cached.onAdminAction === onAdminAction &&
        cached.onRetryFailed === onRetryFailed
      ) {
        nextCache.set(node.id, cached);
        return cached.decorated;
      }
      const decorated = {
        ...node,
        data: {
          ...node.data,
          nodeId: node.id,
          nodeExecutionDirectives: progress.nodeExecutionDirectives,
          onAdminAction,
          // onRetryFailed は AI チェックノードだけが使う。
          ...(node.type === "ai-check" ? { onRetryFailed } : {}),
        },
      } as PipelineNode;
      nextCache.set(node.id, {
        source: node,
        decorated,
        nodeExecutionDirectives: progress.nodeExecutionDirectives,
        onAdminAction,
        onRetryFailed,
      });
      return decorated;
    });
    decorationCacheRef.current = nextCache;
    return result;
  }, [nodes, onAdminAction, onRetryFailed, progress.nodeExecutionDirectives]);
  const handleNodeDoubleClick = useCallback<NodeMouseHandler>((event) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  return (
    <div className="flex flex-col">
      <div style={{ height }} className="w-full">
        <ReactFlow
          nodes={decoratedNodes}
          edges={edges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onNodesChange={onNodesChange}
          onNodeDoubleClick={handleNodeDoubleClick}
          nodesDraggable={false}
          nodesConnectable={false}
          panOnDrag={true}
          zoomOnScroll={true}
          zoomOnPinch={true}
          zoomOnDoubleClick={true}
          fitView
          fitViewOptions={FIT_VIEW_OPTIONS}
          minZoom={0.15}
          maxZoom={2}
          proOptions={PRO_OPTIONS}
        >
          <Background gap={16} size={1} color="#f1f5f9" />
          <Controls
            showInteractive={false}
            position="top-right"
            className="pipeline-controls"
          />
          <MiniMap
            pannable
            zoomable
            nodeStrokeWidth={2}
            nodeColor={(node) => {
              const status = (node.data as { status?: PipelineNodeStatus })
                ?.status;
              return status ? MINIMAP_NODE_COLORS[status] : "#e5e7eb";
            }}
            style={MINIMAP_STYLE}
          />
        </ReactFlow>
      </div>
      {/* Resize handle */}
      <div
        className="pipeline-resize-handle flex items-center justify-center h-3 bg-gray-50 hover:bg-gray-100 border-t border-gray-200 transition-colors"
        onPointerDown={handlePointerDown}
      >
        <div className="w-8 h-1 rounded-full bg-gray-300" />
      </div>
    </div>
  );
}

export const PipelineGraph = memo(function PipelineGraph(
  props: PipelineGraphProps,
) {
  const [direction, setDirection] = useState<LayoutDirection>("LR");

  const handleDirectionChange = useCallback((d: LayoutDirection) => {
    setDirection(d);
  }, []);

  return (
    <DirectionContext.Provider value={direction}>
      <div className="relative">
        <DirectionToggle
          direction={direction}
          onChange={handleDirectionChange}
        />
        {props.progress.graphSkeleton ? (
          <ReactFlowProvider>
            <PipelineGraphInner {...props} direction={direction} />
          </ReactFlowProvider>
        ) : (
          <div className="px-4 py-6 text-sm text-gray-600">
            Waiting for server graph skeleton...
          </div>
        )}
      </div>
    </DirectionContext.Provider>
  );
});
